import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Calendar, Clock, Newspaper } from "lucide-react";
import BeritaModal from "./BeritaModal"; // pastikan path sesuai
import { motion, AnimatePresence } from "framer-motion";
import { rtdb } from "../../../config/firebase";
import { ref, onValue, push, update, remove } from "firebase/database";

// --- Variants Animasi ---
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
};

// --- Komponen Card Berita ---
const BeritaCard = ({ berita, onEdit, onDelete }) => {
  const dateTime = new Date(`${berita.tanggal}T${berita.waktu}`);
  const formattedDate = dateTime.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const formattedTime = dateTime.toLocaleTimeString("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      layout
      className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-md overflow-hidden border border-white/50 hover:shadow-xl hover:border-green-300 transition-all duration-300 flex flex-col sm:flex-row"
    >
      {/* Foto bisa diklik */}
      <div
        className="sm:w-48 flex-shrink-0 cursor-pointer"
        onClick={() => window.open(berita.fotoUrl, "_blank")}
      >
        <img
          src={berita.fotoUrl}
          alt={berita.judul}
          className="w-full h-40 sm:h-full object-cover"
        />
      </div>

      <div className="p-5 flex flex-col flex-grow">
        <h3 className="font-bold text-xl text-gray-800 mb-1">{berita.judul}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2 flex-grow">{berita.keterangan}</p>
        <div className="flex items-center space-x-4 text-xs text-green-800 font-medium bg-green-50 px-3 py-1.5 rounded-full self-start">
          <span className="flex items-center gap-1.5"><Calendar size={14} />{formattedDate}</span>
          <span className="text-green-300">|</span>
          <span className="flex items-center gap-1.5"><Clock size={14} />{formattedTime} WIT</span>
        </div>
      </div>

      <div className="flex sm:flex-col items-center justify-end sm:justify-center p-3 sm:p-4 space-x-2 sm:space-x-0 sm:space-y-2 bg-gray-50/50 border-t sm:border-t-0 sm:border-l border-gray-100">
        <button
          onClick={() => onEdit(berita)}
          className="p-2.5 rounded-full text-blue-600 bg-blue-100 hover:bg-blue-200 hover:scale-110 transition-all"
          title="Edit Berita"
        >
          <Pencil size={18} />
        </button>
        <button
          onClick={() => onDelete(berita.id)}
          className="p-2.5 rounded-full text-red-600 bg-red-100 hover:bg-red-200 hover:scale-110 transition-all"
          title="Hapus Berita"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </motion.div>
  );
};

// --- Komponen Utama ---
export default function KelolaBerita() {
  const [beritaList, setBeritaList] = useState([]);
  const [editingBerita, setEditingBerita] = useState(null);

  // Load data realtime dari Firebase
  useEffect(() => {
    const beritaRef = ref(rtdb, "berita");
    const unsubscribe = onValue(beritaRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.keys(data).map((key) => ({ id: key, ...data[key] }));
        // Urutkan terbaru di atas
        list.sort((a, b) => new Date(b.tanggal + "T" + b.waktu) - new Date(a.tanggal + "T" + a.waktu));
        setBeritaList(list);
      } else {
        setBeritaList([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleOpenModal = (berita = null) => {
    setEditingBerita(
      berita || {
        id: null,
        judul: "",
        keterangan: "",
        tanggal: new Date().toISOString().substring(0, 10),
        waktu: "08:00",
        fotoUrl: "",
      }
    );
  };

  const handleSave = async (id, newData) => {
    try {
      if (id) {
        await update(ref(rtdb, "berita/" + id), newData);
      } else {
        await push(ref(rtdb, "berita"), newData);
      }
      setEditingBerita(null);
    } catch (err) {
      console.error("Gagal menyimpan berita:", err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Yakin ingin menghapus berita ini?")) {
      try {
        await remove(ref(rtdb, "berita/" + id));
      } catch (err) {
        console.error("Gagal menghapus berita:", err);
      }
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50">
      <main className="relative z-10 p-4 md:p-6">
        <motion.header
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="sticky top-4 z-20 bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 mb-8 p-4"
        >
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-500">
                  Kelola
                </span>{" "}
                Berita Gereja
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Publikasikan dan atur semua berita & pengumuman.
              </p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-teal-600 text-white font-semibold rounded-lg shadow-md hover:from-green-600 hover:to-teal-700 transition-all duration-300 transform hover:scale-105"
            >
              <Plus size={18} />
              <span>Tambah Berita Baru</span>
            </button>
          </div>
        </motion.header>

        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <AnimatePresence>
            {beritaList.length > 0 ? (
              <div className="space-y-5">
                {beritaList.map((b) => (
                  <BeritaCard
                    key={b.id}
                    berita={b}
                    onEdit={handleOpenModal}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16 px-6 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 bg-white/60 backdrop-blur-lg"
              >
                <Newspaper className="mx-auto mb-4 text-gray-300" size={48} strokeWidth={1.5} />
                <h3 className="text-xl font-semibold text-gray-700">Belum Ada Berita</h3>
                <p className="mt-1 text-base">Klik tombol "Tambah Berita Baru" untuk memulai.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </main>

      {/* Modal */}
      <AnimatePresence>
        {editingBerita && (
          <BeritaModal
            berita={editingBerita}
            onSave={handleSave}
            onClose={() => setEditingBerita(null)}
            isNew={!editingBerita.id}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
